import {
  Box,
  Checkbox,
  CheckboxGroup,
  HStack,
  Text,
  VStack
} from "@chakra-ui/react"
import { CheckIcon, MinusIcon } from "@pathwright/pathicons"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { Fragment, forwardRef, useMemo } from "react"
import { Virtuoso } from "react-virtuoso"
import PeopleItem from "./PeopleItem"
import SelectItemRole from "./SelectItemRole"
import { useSelectPeopleContext } from "./SelectPeopleContext"
import {
  getDisabledItemLabel,
  getIsValidItem
} from "./utils"

const tPrefix = "share_card.add_tab"

const SelectAllItems = () => {
  const {
    itemsOfQueued: items,
    peopleSelection: { pending },
    addToPeopleSelection,
    removeFromPeopleSelection
  } = useSelectPeopleContext()

  const { tc } = useTranslate()

  const handleCheckAll = (isChecked) => {
    if (isChecked) {
      addToPeopleSelection(items)
    } else {
      removeFromPeopleSelection(items)
    }
  }

  const validCount = useMemo(() => items.filter(getIsValidItem).length, [items])
  const isValid = validCount > 0
  const allChecked = isValid && validCount === pending.length
  const isIndeterminate = !!pending.length && !allChecked

  return (
    <Checkbox.Root
      onChange={(e) => handleCheckAll(e.target.checked)}
      onKeyUp={(e) => {
        if (e.keyCode === 13) {
          handleCheckAll(!e.target.checked)
        }
      }}
      checked={isIndeterminate ? "indeterminate" : allChecked}
      disabled={!isValid}
      w="100%"
      pl={4}
      py={4}
      colorPalette="brand"
      variant="solid"
    >
      <Checkbox.HiddenInput />
      <Checkbox.Control
        // NOTE: a border color of "border.emphasized" is automatically applied, but this isn't
        // resolving to an actual color value (a bug exists somewhere).
        borderColor="colorPalette.600"
      >
        <Checkbox.Indicator>
          {
            // For some reason the disabled checkbox shows a checkmark even though isChecked is false??
            isValid ? (
              isIndeterminate ? (
                <MinusIcon />
              ) : (
                <CheckIcon />
              )
            ) : (
              <Fragment />
            )
          }
        </Checkbox.Indicator>
      </Checkbox.Control>
      <Checkbox.Label>{tc(`${tPrefix}.Select all`)}</Checkbox.Label>
    </Checkbox.Root>
  )
}

const Item = ({ index, item, isChecked }) => {
  const { addToPeopleSelection, removeFromPeopleSelection } =
    useSelectPeopleContext()

  const { tc } = useTranslate()

  const isValid = getIsValidItem(item)

  const handleCheck = (item, isChecked) => {
    if (isChecked) {
      addToPeopleSelection(item)
    } else {
      removeFromPeopleSelection(item)
    }
  }

  return (
    <HStack width="100%" justifyContent="space-between" px={4} pl={4}>
      <Checkbox.Root
        value={item.id}
        // For some reason the disabled checkbox shows a checkmark even though checked is false??
        icon={isValid ? <CheckIcon /> : <Fragment />}
        flexGrow={1}
        minW={0}
        onChange={(e) => handleCheck(item, e.target.checked)}
        onKeyUp={(e) => {
          if (e.keyCode === 13) {
            handleCheck(item, !e.target.checked)
          }
        }}
        checked={isChecked}
        disabled={!isValid}
        width="100%"
      >
        <Checkbox.HiddenInput />
        <Checkbox.Control
          // NOTE: specifying colorPalette and variant here to avoid overriding UserAvatar styles.
          colorPalette="brand"
          variant="solid"
          // NOTE: a border color of "border.emphasized" is automatically applied, but this isn't
          // resolving to an actual color value (a bug exists somewhere).
          borderColor="colorPalette.600"
        />
        <Checkbox.Label w="100%">
          <HStack
            width="100%"
            justifyContent="flex-start"
            data-testId={`selected-queued-people-item-${index}`}
          >
            <PeopleItem item={item} />
            {getDisabledItemLabel(item, tc) && (
              <Text color="gray.500">{getDisabledItemLabel(item, tc)}</Text>
            )}
          </HStack>
        </Checkbox.Label>
      </Checkbox.Root>
      {isValid && (
        <Box flexShrink={0}>
          <SelectItemRole item={item} />
        </Box>
      )}
    </HStack>
  )
}

// Sole goal of overriding the Scroller is to move the groupContent component
// to render before the itemContent. This results in better Accessibility as
// tabbing into the Virtuoso items will first go to the "Select all" checkbox
// rather than the first item.
const Scroller = forwardRef((props, ref) => (
  <Box
    ref={ref}
    {...props}
    borderRadius="inherit"
    // Styling the Group component, though technically a parent of the Group and
    // unfortunately unable to style the parent otherwise.
    css={{
      // Layer Group over items.
      "& > div:first-child": {
        backgroundColor: "white",
        position: "relative",
        zIndex: 10
      }
    }}
  >
    {props.children.slice().reverse()}
  </Box>
))

const List = ({ items, pending }) => (
  <Box
    w="100%"
    css={{
      // Fix scroll jank when interacting with an open MenuList near bottom of scroll container.
      "& [data-virtuoso-scroller='true'], & [data-viewport-type='element']": {
        position: "static !important"
      },
      ["& [data-viewport-type='element'] > div"]: {
        // Counteract the padding applied to the list which is meant to accomodate the groupContent
        // but with static positioning, results in unwanted space. Unfortunately must hard-code. Resetting
        // paddingTop to 0 results in space at the bottom of the list, and Virtuosos updates paddign top and bottom
        // when accounting for non-rendered items in the list.
        marginTop: "-51px !important"
      }
    }}
  >
    <Virtuoso
      style={{
        height: "300px",
        width: "100%"
      }}
      components={{ Scroller }}
      groupCounts={[items.length]}
      groupContent={(index) => <SelectAllItems />}
      itemContent={(index) => (
        <Item
          index={index}
          item={items[index]}
          isChecked={pending.some(
            (pendingItem) => pendingItem.id === items[index].id
          )}
        />
      )}
    />
  </Box>
)

const SelectQueuedPeople = () => {
  const {
    itemsOfQueued: items,
    peopleSelection: { pending }
  } = useSelectPeopleContext()

  return (
    <VStack gap={2} alignItems="flex-start" borderRadius="inherit">
      <CheckboxGroup value={pending.map((item) => item.id)} w="100%">
        {!!items.length && <List items={items} pending={pending} />}
      </CheckboxGroup>
    </VStack>
  )
}

export default SelectQueuedPeople
